import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

import { focusRing } from '../styles/focus-ring';

export const segmentedControlListVariants = cva(
  cn(
    // Forward-compat group hook for descendants.
    'group/segmented-control-list',
    // `relative` anchors any positioned descendants; `isolate` seals the list as its own
    // stacking context so subsequent z-indexed children don't have to fight ancestor layers.
    'relative isolate inline-flex items-center justify-center',
    // Sizing & spacing — `w-fit` so the list hugs its triggers.
    'w-fit p-1',
    // Shape & color
    'rounded-lg bg-background-tertiary text-text-secondary',
    // Orientation-aware sizing (driven by data-orientation on the SegmentedControl root).
    'group-data-[orientation=horizontal]/segmented-control:h-9',
    'group-data-[orientation=vertical]/segmented-control:h-fit',
    'group-data-[orientation=vertical]/segmented-control:flex-col'
  ),
  {
    variants: {
      variant: {
        default: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export const segmentedControlIndicatorClasses = cn(
  // Anchored at the list's top-left; geometry is driven imperatively by the FLIP effect in
  // SegmentedControlList. `left-0` must stay physical — `offsetLeft` is always physical, and
  // `start-0` would flip the anchor in RTL while the measurement does not, putting the indicator
  // on the wrong side. Disable suppresses the rule's autofix, which would silently re-break this.
  // eslint-disable-next-line @heidi/rules/enforce-logical-direction -- physical anchor required for FLIP math
  'pointer-events-none absolute left-0 top-0 z-0 rounded-md bg-surface',
  // FLIP invert math requires scale to grow from the top-left, not the default centre.
  'origin-top-left',
  // `box-shadow` rides the compositor layer with the transform — no per-frame paint cost during the slide.
  // Alphas track `--shadow-sm` bumped for warm-on-warm visibility.
  '[box-shadow:0_1px_3px_rgb(var(--shadow-color)/0.12),0_1px_1px_rgb(var(--shadow-color)/0.06)]',
  // `transition-transform` is compositor-eligible — immune to main-thread work on heavy pages.
  // `ease-in-out-quart` per animations.md.
  'transition-transform duration-200 ease-in-out-quart',
  'motion-reduce:transition-none'
);

export const segmentedControlTriggerVariants = cva(
  cn(
    // Stacking — `z-10` keeps the label above the sliding indicator (`z-0`).
    'relative z-10',
    // Layout
    'inline-flex flex-1 items-center justify-center',
    // Size & spacing — h-7 = 28px
    'h-7 gap-1.5 px-2.5',
    // Shape — `border-2 border-transparent` keeps the trigger's box size stable across states.
    'rounded-md border-2 border-transparent',
    // Typography
    'whitespace-nowrap text-sm font-normal',
    // Color states — active pill bg/shadow are painted by the indicator (see SegmentedControlList).
    'text-text-secondary hover:text-text-primary data-[state=active]:text-text-primary',
    // Focus & disabled — shared subtle ring (outset box-shadow, no layout shift).
    focusRing,
    'disabled:pointer-events-none disabled:opacity-50',
    // Transitions
    'transition-colors duration-200 ease-out',
    // Child icons
    '[&_svg:not([class*="size-"])]:size-3.5 [&_svg]:pointer-events-none [&_svg]:shrink-0'
  ),
  {
    variants: {
      iconOnly: {
        true: 'w-7 flex-none px-0',
        // Vertical orientation: stretch full-width, don't flex-grow, left-align label.
        false: cn(
          'group-data-[orientation=vertical]/segmented-control:w-full',
          'group-data-[orientation=vertical]/segmented-control:flex-none',
          'group-data-[orientation=vertical]/segmented-control:justify-start'
        ),
      },
    },
    defaultVariants: {
      iconOnly: false,
    },
  }
);

export const segmentedControlContentClasses = cn(
  // Layout — fills space below the list (horizontal) or beside it (vertical).
  'flex-1',
  // Radix leaves the inactive panel mounted under `hidden`, which a consumer `flex` outranks,
  // so the empty panel pushes the active one down; attribute + class out-specifies it. Also
  // hides `forceMount` panels, so an exit animation there needs an opt-out.
  'data-[state=inactive]:hidden',
  // Shape — `border-2 border-transparent` keeps the box size stable across states.
  'rounded-md border-2 border-transparent',
  // Typography
  'text-sm',
  // Focus — shared subtle ring (outset box-shadow, no layout shift).
  focusRing
);
